import TOUR_IS_DEV from '../env';
import useTourController from '../hooks/useTourController';
import TourAppEventSlot from './TourAppEventSlot';
import TourAutoAdvanceSlot from './TourAutoAdvanceSlot';
import TourBannerSlot from './TourBannerSlot';
import TourMediaCloseWatcher from './TourMediaCloseWatcher';
import TourModalSlot from './TourModalSlot';
import TourSpotlightSlot from './TourSpotlightSlot';

/**
 * Drives one tour and composes its component set. Logic only — it owns the controller and the
 * resolved config, then mounts whichever components the current step declares. Every advance
 * mechanism is its own headless component; none know about arbitration or path resolution.
 */
const TourRunner = ({ tourId }: { tourId: string }) => {
  const { isActive, currentStepId, ui, handleButtonClick, handleAppEvent, advance } = useTourController(tourId, {
    isDev: TOUR_IS_DEV,
  });

  if (!isActive) return null;

  return (
    <>
      {ui.modal && <TourModalSlot modal={ui.modal} advanceTo={advance} onButtonClick={handleButtonClick} />}
      {ui.banner && !ui.modal && <TourBannerSlot banner={ui.banner} onButtonClick={handleButtonClick} />}
      {ui.spotlight && (
        <TourSpotlightSlot
          key={`${currentStepId}-${ui.spotlight.target}`}
          target={ui.spotlight.target}
          scrollIntoView={ui.spotlight.scrollIntoView}
        />
      )}
      {/* Several at once: a step may wait on more than one action, each with its own target. */}
      {ui.onAppEvent.map((config) => (
        <TourAppEventSlot key={`${currentStepId}-${config.event}`} config={config} onFire={handleAppEvent} />
      ))}
      {ui.autoAdvance && (
        <TourAutoAdvanceSlot
          key={currentStepId}
          delayMs={ui.autoAdvance.delayMs}
          onAdvance={() => advance(ui.autoAdvance!.targetStepId)}
        />
      )}
      {ui.onMediaClose && <TourMediaCloseWatcher onMediaClose={() => advance(ui.onMediaClose!.targetStepId)} />}
    </>
  );
};

export default TourRunner;
